import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, User, Building2, IndianRupee, StickyNote, ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/theme";

const STEPS = [
  { id: 1, label: "Contact", icon: User },
  { id: 2, label: "Project", icon: Building2 },
  { id: 3, label: "Sales", icon: IndianRupee },
  { id: 4, label: "Notes", icon: StickyNote },
];

const empty = {
  name: "", company_name: "", company_id: "", contact_person: "", phone: "", email: "", location: "",
  industry: "", estimated_area: "", project_type: "", flooring_system: "", expected_project_date: "",
  estimated_value: "", lead_source: "", priority: "Medium", assigned_to: "", stage: "New Lead",
  next_follow_up: "", notes: "",
  project_name: "", total_contracted_area: "", quoted_rate: "", quoted_value: "",
  quoted_value_overridden: false, quote_date: "", quote_valid_until: "",
  expected_start_date: "", expected_completion_date: "", scope_items: [],
  cgst_pct: "9", sgst_pct: "9",
};

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      {children}
    </div>
  );
}

export function LeadFormDialog({ open, onOpenChange, lead, onSaved }) {
  const { meta, user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const editing = !!lead;

  useEffect(() => {
    if (open) {
      setStep(1);
      setErrors({});
      if (lead) {
        const seededItems = (lead.scope_items && lead.scope_items.length)
          ? lead.scope_items.map((i) => ({ system: i.system || "", area: i.area || "", rate: i.rate || "" }))
          : (lead.total_contracted_area || lead.quoted_rate
              ? [{ system: lead.flooring_system || "", area: lead.total_contracted_area || "", rate: lead.quoted_rate || "" }]
              : []);
        setForm({ ...empty, ...lead, scope_items: seededItems, estimated_area: lead.estimated_area || "", estimated_value: lead.estimated_value || "",
          expected_project_date: (lead.expected_project_date || "").slice(0, 10), next_follow_up: (lead.next_follow_up || "").slice(0, 10) });
      } else {
        setForm({ ...empty, assigned_to: user?.id || "" });
      }
      api.get("/companies?limit=200").then((r) => setCompanies(r.data.items)).catch(() => {});
    }
  }, [open, lead, user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const items = form.scope_items || [];
  const itemsArea = items.reduce((s, i) => s + (Number(i.area) || 0), 0);
  const itemsValue = items.reduce((s, i) => s + (Number(i.area) || 0) * (Number(i.rate) || 0), 0);

  const setItem = (idx, k, v) => setForm((f) => ({ ...f, scope_items: f.scope_items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((f) => ({ ...f, scope_items: [...(f.scope_items || []), { system: "", area: "", rate: "" }] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, scope_items: f.scope_items.filter((_, i) => i !== idx) }));

  const autoQuoted = form.quoted_value_overridden
    ? form.quoted_value
    : (itemsValue > 0 ? Math.round(itemsValue) : "");

  const leadQv = Number(form.quoted_value_overridden ? form.quoted_value : autoQuoted) || 0;
  const leadCgstAmt = Math.round(leadQv * (Number(form.cgst_pct) || 0)) / 100;
  const leadSgstAmt = Math.round(leadQv * (Number(form.sgst_pct) || 0)) / 100;
  const leadGrandTotal = leadQv + leadCgstAmt + leadSgstAmt;

  const onCompanySelect = (cid) => {
    const c = companies.find((x) => x.id === cid);
    setForm((f) => ({ ...f, company_id: cid, company_name: c ? c.name : f.company_name,
      industry: c?.industry || f.industry, location: c?.location || f.location }));
  };

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!form.company_name.trim()) e.company_name = "Company name is required";
      if (form.phone && !/^(\+91[\s-]?)?[6-9]\d{9}$/.test(form.phone.replace(/[\s-]/g, "").replace(/^\+91/, "+91"))) {
        const digits = form.phone.replace(/[^0-9]/g, "");
        if (!(digits.length === 10 && /^[6-9]/.test(digits)) && !(digits.length === 12 && digits.startsWith("91"))) e.phone = "Enter a valid Indian phone number";
      }
      if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Enter a valid email";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep(step)) setStep((s) => Math.min(4, s + 1)); };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!validateStep(1)) { setStep(1); return; }
    setSaving(true);
    const payload = {
      ...form,
      name: form.name.trim() || `${form.flooring_system || "Flooring"} — ${form.company_name}`,
      estimated_area: Number(form.estimated_area) || 0,
      estimated_value: Number(form.estimated_value) || 0,
      scope_items: (form.scope_items || [])
        .filter((i) => i.system || Number(i.area) || Number(i.rate))
        .map((i) => ({ system: i.system || null, area: Number(i.area) || 0, rate: Number(i.rate) || 0 })),
      total_contracted_area: itemsArea || Number(form.total_contracted_area) || 0,
      quoted_rate: itemsArea > 0 ? Math.round((itemsValue / itemsArea) * 100) / 100 : Number(form.quoted_rate) || 0,
      quoted_value: Number(form.quoted_value_overridden ? form.quoted_value : autoQuoted) || 0,
      cgst_pct: Number(form.cgst_pct) || 0,
      sgst_pct: Number(form.sgst_pct) || 0,
      quoted_value_overridden: !!form.quoted_value_overridden,
      company_id: form.company_id || null,
      assigned_to: form.assigned_to || null,
      expected_project_date: form.expected_project_date || null,
      next_follow_up: form.next_follow_up || null,
    };
    try {
      if (editing) {
        await api.put(`/leads/${lead.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead created successfully");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const users = meta?.users || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0 max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle className="text-lg">{editing ? "Edit Lead" : "Create New Lead"}</DialogTitle>
          <DialogDescription className="sr-only">Capture contact, project scope, sales and notes for this lead.</DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 border-b border-slate-100 bg-slate-50/60 px-6 py-3">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = step === s.id;
            const done = step > s.id;
            return (
              <div key={s.id} className="flex flex-1 items-center gap-2">
                <button onClick={() => step > s.id && setStep(s.id)} className="flex items-center gap-2">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition ${done ? "bg-emerald-500 text-white" : active ? "text-white" : "bg-white text-slate-400 border border-slate-200"}`}
                    style={active ? { backgroundColor: "#0057A8" } : {}}>
                    {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                  </div>
                  <span className={`hidden text-xs font-semibold sm:block ${active ? "text-slate-800" : "text-slate-400"}`}>{s.label}</span>
                </button>
                {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 rounded ${done ? "bg-emerald-500" : "bg-slate-200"}`} />}
              </div>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {step === 1 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 animate-fade-up">
              <div className="sm:col-span-2">
                <Field label="Company Name" required>
                  <Input data-testid="lead-company-name" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="e.g. ABC Food Processing Pvt Ltd" />
                  {errors.company_name && <p className="text-xs text-red-500">{errors.company_name}</p>}
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Link to Existing Company (optional)">
                  <Select value={form.company_id || "none"} onValueChange={(v) => onCompanySelect(v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="lead-company-link"><SelectValue placeholder="Select company" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not linked</SelectItem>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Contact Person">
                <Input data-testid="lead-contact-person" value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="Full name" />
              </Field>
              <Field label="Phone">
                <Input data-testid="lead-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98470 00000" />
                {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
              </Field>
              <Field label="Email">
                <Input data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@company.com" />
                {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
              </Field>
              <Field label="Location">
                <Input data-testid="lead-location" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="City, State" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 animate-fade-up">
              <Field label="Industry">
                <Select value={form.industry} onValueChange={(v) => set("industry", v)}>
                  <SelectTrigger data-testid="lead-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                  <SelectContent>{(meta?.industries || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Estimated Area (Sq.Ft)">
                <Input data-testid="lead-area" type="number" value={form.estimated_area} onChange={(e) => set("estimated_area", e.target.value)} placeholder="e.g. 25000" />
              </Field>
              <Field label="Project Type">
                <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
                  <SelectTrigger data-testid="lead-project-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {["New Construction", "Renovation", "Repair & Recoat", "Maintenance"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Required Flooring System">
                <Select value={form.flooring_system} onValueChange={(v) => set("flooring_system", v)}>
                  <SelectTrigger data-testid="lead-flooring"><SelectValue placeholder="Select system" /></SelectTrigger>
                  <SelectContent>{(meta?.flooring_systems || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Expected Project Date">
                <Input data-testid="lead-expected-date" type="date" value={form.expected_project_date} onChange={(e) => set("expected_project_date", e.target.value)} />
              </Field>
              <Field label="Estimated Project Value (₹)">
                <Input data-testid="lead-value" type="number" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value)} placeholder="e.g. 5500000" />
              </Field>

              <div className="sm:col-span-2 mt-2 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
                <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider" style={{ color: "#0057A8" }}>Commercial &amp; Contract</div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Project Name">
                    <Input data-testid="lead-project-name" value={form.project_name} onChange={(e) => set("project_name", e.target.value)} placeholder="e.g. Warehouse Epoxy Flooring" />
                  </Field>
                </div>

                <div className="mt-4">
                  <div className="mb-2 flex items-center justify-between">
                    <Label className="text-xs font-semibold text-slate-600">Scope of Work (add each system)</Label>
                    <Button type="button" data-testid="add-scope-item" onClick={addItem} size="sm" variant="outline" className="h-7 gap-1 text-xs"><Plus className="h-3.5 w-3.5" /> Add System</Button>
                  </div>
                  {items.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 bg-white py-6 text-center">
                      <p className="text-xs text-slate-400">No systems added. Add 2mm / 3mm epoxy, coating, line marking etc. as separate lines.</p>
                      <Button type="button" data-testid="add-scope-item-empty" onClick={addItem} size="sm" className="mt-3 gap-1 text-xs"><Plus className="h-3.5 w-3.5" /> Add First System</Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="hidden grid-cols-[1fr_90px_90px_110px_32px] gap-2 px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:grid">
                        <span>System</span><span className="text-right">Area</span><span className="text-right">₹/Sq.Ft</span><span className="text-right">Amount</span><span></span>
                      </div>
                      {items.map((it, idx) => {
                        const amount = (Number(it.area) || 0) * (Number(it.rate) || 0);
                        return (
                          <div key={idx} data-testid={`scope-item-${idx}`} className="grid grid-cols-2 items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 sm:grid-cols-[1fr_90px_90px_110px_32px]">
                            <Select value={it.system} onValueChange={(v) => setItem(idx, "system", v)}>
                              <SelectTrigger data-testid={`scope-system-${idx}`} className="col-span-2 h-9 sm:col-span-1"><SelectValue placeholder="Select system" /></SelectTrigger>
                              <SelectContent>{(meta?.flooring_systems || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input data-testid={`scope-area-${idx}`} type="number" value={it.area} onChange={(e) => setItem(idx, "area", e.target.value)} placeholder="Area" className="h-9 text-right" />
                            <Input data-testid={`scope-rate-${idx}`} type="number" value={it.rate} onChange={(e) => setItem(idx, "rate", e.target.value)} placeholder="Rate" className="h-9 text-right" />
                            <div className="flex h-9 items-center justify-end px-1 font-mono text-xs font-semibold text-slate-700">₹{amount.toLocaleString("en-IN")}</div>
                            <button type="button" data-testid={`remove-scope-item-${idx}`} onClick={() => removeItem(idx)} className="flex h-9 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-end gap-4 px-1 pt-1 text-xs text-slate-500">
                        <span>Total Area: <span className="font-mono font-semibold text-slate-700">{itemsArea.toLocaleString("en-IN")} Sq.Ft</span></span>
                        <span>Total: <span data-testid="scope-total" className="font-mono font-semibold text-slate-800">₹{Math.round(itemsValue).toLocaleString("en-IN")}</span></span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Quoted Total Value (₹)">
                    <div className="relative">
                      <Input data-testid="lead-quoted-value" type="number" value={autoQuoted} disabled={!form.quoted_value_overridden} onChange={(e) => set("quoted_value", e.target.value)} className={form.quoted_value_overridden ? "" : "bg-slate-100"} />
                    </div>
                    <label className="mt-1 flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
                      <input type="checkbox" data-testid="lead-override-toggle" checked={!!form.quoted_value_overridden} onChange={(e) => set("quoted_value_overridden", e.target.checked)} />
                      Override auto-calculated total
                    </label>
                    {form.quoted_value_overridden && <p className="text-[11px] font-semibold text-amber-600">⚠ Manually overridden</p>}
                  </Field>
                  <Field label="CGST %"><Input data-testid="lead-cgst" type="number" value={form.cgst_pct} onChange={(e) => set("cgst_pct", e.target.value)} /></Field>
                  <Field label="SGST %"><Input data-testid="lead-sgst" type="number" value={form.sgst_pct} onChange={(e) => set("sgst_pct", e.target.value)} /></Field>
                  <Field label="Total incl. GST (₹)">
                    <div data-testid="lead-grand-total" className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-sm font-semibold text-slate-800">₹{leadGrandTotal.toLocaleString("en-IN")}</div>
                    <p className="mt-1 text-[11px] text-slate-400">CGST ₹{leadCgstAmt.toLocaleString("en-IN")} + SGST ₹{leadSgstAmt.toLocaleString("en-IN")}</p>
                  </Field>
                  <Field label="Quote Date"><Input type="date" value={form.quote_date} onChange={(e) => set("quote_date", e.target.value)} /></Field>
                  <Field label="Quote Valid Until"><Input type="date" value={form.quote_valid_until} onChange={(e) => set("quote_valid_until", e.target.value)} /></Field>
                  <Field label="Expected Project Start"><Input type="date" value={form.expected_start_date} onChange={(e) => set("expected_start_date", e.target.value)} /></Field>
                  <Field label="Expected Completion"><Input type="date" value={form.expected_completion_date} onChange={(e) => set("expected_completion_date", e.target.value)} /></Field>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 animate-fade-up">
              <Field label="Lead Source">
                <Select value={form.lead_source} onValueChange={(v) => set("lead_source", v)}>
                  <SelectTrigger data-testid="lead-source"><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>{(meta?.sources || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Priority">
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger data-testid="lead-priority"><SelectValue placeholder="Select priority" /></SelectTrigger>
                  <SelectContent>{(meta?.priorities || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Assigned Salesperson">
                <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
                  <SelectTrigger data-testid="lead-assigned"><SelectValue placeholder="Select salesperson" /></SelectTrigger>
                  <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Stage">
                <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                  <SelectTrigger data-testid="lead-stage"><SelectValue /></SelectTrigger>
                  <SelectContent>{(meta?.stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Next Follow-up Date">
                  <Input data-testid="lead-followup" type="date" value={form.next_follow_up} onChange={(e) => set("next_follow_up", e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="animate-fade-up">
              <Field label="Additional Notes">
                <Textarea data-testid="lead-notes" rows={7} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Requirement details, site conditions, competitor info, budget notes…" />
              </Field>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={prev} disabled={step === 1} className="gap-1"><ChevronLeft className="h-4 w-4" /> Back</Button>
          {step < 4 ? (
            <Button data-testid="lead-next-step" onClick={next} className="gap-1 font-semibold">Continue <ChevronRight className="h-4 w-4" /></Button>
          ) : (
            <Button data-testid="lead-submit" onClick={submit} disabled={saving} className="gap-1 font-semibold">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Lead"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
